import React, { useEffect, useState, useCallback } from "react";
import Modal1 from "./Modal1";
import Icon1 from "../icons/Icon";
import TextField2T from "../textfields/TextField2T";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import EquipmentTypeSelect from "./EquipmentTypeSelect";
import { getDocDataById } from "../models/DataFunctions";
import getEquipmentFilesUrls from "../api/presigned/getEquipmentFilesUrls";
import getBookshelfEquipmentFilesUrls from "../api/presigned/getBookshelfEquipmentFilesUrls";
import { useSnackbar } from "notistack";
import Spinner from "../loading/Spinner";
import updateEquipment from "../api/equipment/updateEquipment";
import updateEquipmentFiles from "../api/equipment/updateEquipmentFiles";
import updateBookshelfEquipment from "../api/equipment/updateBookshelfEquipment";
import updateBookshelfEquipmentFiles from "../api/equipment/updateBookshelfEquipmentFiles";
import addEquipmentToBookshelf from "../api/equipment/addEquipmentToBookshelf";
import deleteDocApi from "../api/database/deleteDocApi";
import { queryCollection } from "../models/DataFunctions";
import { db } from "../config/FirebaseConfig";
import { doc } from "firebase/firestore";
import SmallSpinner from "../loading/SmallSpinner";
import { EquipementImage } from "../pages/carnets/EquipementImage";

export const EquipmentModal = ({
  equipmentId,
  open,
  onClose,
  onRefresh,
  prefix,
  isBookshelfMode = false,
  orgId,
}) => {
  const [equipment, setEquipment] = useState(null);
  const [equipmentFilesUrls, setEquipmentFilesUrls] = useState([]);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [originalMaintenanceTasks, setOriginalMaintenanceTasks] = useState([]);
  const [contacts, setContacts] = useState([]); // ✅ Separate contacts state
  const [originalContacts, setOriginalContacts] = useState([]); // ✅ Original contacts for cancel
  const [isEditing, setIsEditing] = useState(false);
  const [editedEquipment, setEditedEquipment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddField, setShowAddField] = useState(false);
  const [showMaintenanceHistory, setShowMaintenanceHistory] = useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    type: "",
    tel: "",
    email: "",
  });
  const [newField, setNewField] = useState({ label: "", value: "" });
  const [deletedTaskIds, setDeletedTaskIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInBookshelf, setIsInBookshelf] = useState(false);
  const [bookshelfEquipmentId, setBookshelfEquipmentId] = useState(null);
  const [bookshelfLoading, setBookshelfLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  // ✅ Utility function to normalize dates to timestamps
  const normalizeToTimestamp = (dateValue) => {
    if (!dateValue) return null;

    try {
      // Handle Firestore timestamp object with seconds and nanoseconds
      if (
        dateValue &&
        typeof dateValue === "object" &&
        "seconds" in dateValue
      ) {
        return (
          dateValue.seconds * 1000 + Math.floor(dateValue.nanoseconds / 1000000)
        );
      }

      // Handle Firestore timestamp with toDate method
      if (dateValue && typeof dateValue.toDate === "function") {
        return dateValue.toDate().getTime();
      }

      // Handle Unix timestamp (number) - return as is
      if (typeof dateValue === "number") {
        return dateValue;
      }

      // Handle date string
      if (typeof dateValue === "string") {
        return new Date(dateValue).getTime();
      }

      // Handle Date object
      if (dateValue instanceof Date) {
        return dateValue.getTime();
      }

      return null;
    } catch (error) {
      console.error("Error normalizing date to timestamp:", error, dateValue);
      return null;
    }
  };

  // ✅ Utility function to normalize all dates in an object
  const normalizeDatesInObject = (obj, dateFields = []) => {
    if (!obj) return obj;

    const normalized = { ...obj };

    // Normalize specific date fields
    dateFields.forEach((field) => {
      if (normalized[field]) {
        normalized[field] = normalizeToTimestamp(normalized[field]);
      }
    });

    return normalized;
  };

  // ✅ Utility function to normalize maintenance tasks dates
  const normalizeMaintenanceTasksDates = (tasks) => {
    if (!Array.isArray(tasks)) return tasks;

    return tasks.map((task) => ({
      ...task,
      last_performed_date: normalizeToTimestamp(task.last_performed_date),
      next_due_date: normalizeToTimestamp(task.next_due_date),
      created_date: normalizeToTimestamp(task.created_date),
      updated_date: normalizeToTimestamp(task.updated_date),
    }));
  };

  const resetState = useCallback(() => {
    setEquipment(null);
    setEquipmentFilesUrls([]);
    setMaintenanceTasks([]);
    setOriginalMaintenanceTasks([]);
    setContacts([]); // ✅ Reset contacts
    setOriginalContacts([]); // ✅ Reset original contacts
    setIsEditing(false);
    setEditedEquipment((prev) => {
      // Revoke all blob URLs to prevent memory leaks
      if (prev?.files) {
        prev.files.forEach((file) => {
          if (file.url && file.url.startsWith("blob:")) {
            URL.revokeObjectURL(file.url);
          }
        });
      }
      return null;
    });
    setShowDeleteConfirm(false);
    setShowAddContact(false);
    setShowAddField(false);
    setShowMaintenanceHistory(false);
    setNewContact({ name: "", type: "", tel: "", email: "" });
    setNewField({ label: "", value: "" });
    setDeletedTaskIds([]);
    setIsLoading(false);
    setIsInBookshelf(false);
    setBookshelfEquipmentId(null);
  }, []);

  const checkBookshelfStatus = useCallback(
    async (equipmentData, orgId) => {
      // Only check bookshelf status for carnet equipment (not bookshelf mode)
      if (isBookshelfMode || !equipmentData) return;

      try {
        // Construct hexagone_id from carnet equipment data
        const hexagoneId = `${equipmentData.name || ""}_${
          equipmentData.equipment_type || ""
        }_${equipmentData.model || ""}_${equipmentData.brand || ""}_${
          orgId || ""
        }`;
        console.log("Checking bookshelf status for hexagone_id:", hexagoneId);
        const filters = [
          { field: "hexagone_id", operator: "==", value: hexagoneId },
          { field: "org_id", operator: "==", value: orgId },
        ];

        const results = await queryCollection("logement_bookshelf", filters);
        if (results && results.length > 0) {
          setIsInBookshelf(true);
          setBookshelfEquipmentId(results[0].id);
        } else {
          setIsInBookshelf(false);
          setBookshelfEquipmentId(null);
        }
      } catch (error) {
        console.error("Error checking bookshelf status:", error);
        setIsInBookshelf(false);
        setBookshelfEquipmentId(null);
      }
    },
    [isBookshelfMode]
  );

  const handleAddToBookshelf = async () => {
    if (isBookshelfMode || !equipment || isInBookshelf) return;

    setBookshelfLoading(true);
    try {
      const bookshelfData = {
        name: equipment.name,
        brand: equipment.brand,
        model: equipment.model,
        equipment_type: equipment.equipment_type,
        notes: equipment.notes,
        num_serie: equipment.num_serie,
        serial_number: equipment.serial_number,
        lifespan: equipment.lifespan,
        maintenance_tasks: equipment.maintenance_tasks || [],
        files: equipment.files || [],
        type: "bookshelf_equipment",
        hexagone_id: `${equipment.name || ""}_${
          equipment.equipment_type || ""
        }_${equipment.model || ""}_${equipment.brand || ""}_${orgId || ""}`,
      };

      const response = await addEquipmentToBookshelf(bookshelfData, prefix);
      if (response && response.id) {
        // If there are files, copy them to the bookshelf equipment
        if (equipment.files && equipment.files.length > 0) {
          try {
            const fileObjects = [];
            const finalFilesIds = [];

            for (const file of equipment.files) {
              if (file.url) {
                try {
                  // Fetch the file content
                  const fileResponse = await fetch(file.url);
                  const blob = await fileResponse.blob();

                  // Create a File object
                  const fileName =
                    file.name || `file_${Date.now()}_${Math.random()}`;
                  const fileObj = new File([blob], fileName, {
                    type: blob.type,
                  });

                  fileObjects.push(fileObj);
                  finalFilesIds.push(
                    file.id || `file_${Date.now()}_${Math.random()}`
                  );
                } catch (fileError) {
                  console.error("Error processing file:", fileError);
                }
              }
            }

            if (fileObjects.length > 0) {
              await updateBookshelfEquipmentFiles(
                response.id,
                fileObjects,
                finalFilesIds,
                prefix
              );
            }
          } catch (fileError) {
            console.error("Error copying files to bookshelf:", fileError);
            // Still show success but with a note about files
            handleClickVariant(
              "Équipement ajouté à la bibliothèque (fichiers non copiés)",
              "warning"
            );
            setIsInBookshelf(true);
            setBookshelfEquipmentId(response.id);
            return;
          }
        }

        setIsInBookshelf(true);
        setBookshelfEquipmentId(response.id);
        handleClickVariant("Équipement ajouté à la bibliothèque", "success");
      }
    } catch (error) {
      console.error("Error adding to bookshelf:", error);
      handleClickVariant("Erreur lors de l'ajout à la bibliothèque", "error");
    } finally {
      setBookshelfLoading(false);
    }
  };

  const handleRemoveFromBookshelf = async () => {
    if (isBookshelfMode || !bookshelfEquipmentId || !isInBookshelf) return;

    setBookshelfLoading(true);
    try {
      const equipmentRef = doc(db, "logement_bookshelf", bookshelfEquipmentId);
      await deleteDocApi(equipmentRef, prefix);

      setIsInBookshelf(false);
      setBookshelfEquipmentId(null);
      handleClickVariant("Équipement retiré de la bibliothèque", "success");
    } catch (error) {
      console.error("Error removing from bookshelf:", error);
      handleClickVariant(
        "Erreur lors de la suppression de la bibliothèque",
        "error"
      );
    } finally {
      setBookshelfLoading(false);
    }
  };

  const handleBookshelfToggle = () => {
    if (isInBookshelf) {
      handleRemoveFromBookshelf();
    } else {
      handleAddToBookshelf();
    }
  };

  const fetchEquipment = useCallback(async () => {
    if (!equipmentId || !open) return;

    try {
      setIsLoading(true);
      const collection = isBookshelfMode
        ? "logement_bookshelf"
        : "carnet_equipment";
      console.log(
        `Fetching equipment from ${collection} for ${
          isBookshelfMode ? "bookshelf" : "carnet"
        } mode`
      );

      const data = await getDocDataById(collection, equipmentId);

      // ✅ Build the complete equipment object step by step
      let normalizedEquipment = normalizeDatesInObject(data, [
        "date_livraison",
      ]);

      // ✅ Handle file URLs based on mode
      if (normalizedEquipment.files && normalizedEquipment.files.length > 0) {
        if (isBookshelfMode) {
          // For bookshelf equipment, generate presigned URLs first (this updates the document)
          try {
            console.log(
              "Generating presigned URLs for bookshelf equipment files"
            );
            await getBookshelfEquipmentFilesUrls(equipmentId, prefix);
            // Fetch the equipment again to get the updated URLs
            const updatedData = await getDocDataById(collection, equipmentId);
            normalizedEquipment = normalizeDatesInObject(updatedData, [
              "date_livraison",
            ]);
            console.log(
              "Using file URLs from updated bookshelf equipment document"
            );
          } catch (urlError) {
            console.error("Error generating presigned URLs:", urlError);
            // Continue without presigned URLs if this fails
          }
        } else {
          // For carnet equipment, fetch and merge file URLs
          try {
            const filesRes = await getEquipmentFilesUrls(equipmentId, prefix);

            // Merge file URLs with existing files
            normalizedEquipment.files = normalizedEquipment.files.map(
              (file) => {
                const fileUrlData = filesRes.find((urlData) => {
                  const [fileId] = Object.entries(urlData)[0];
                  return file.id === fileId;
                });

                if (fileUrlData) {
                  const [, fileUrl] = Object.entries(fileUrlData)[0];
                  return { ...file, url: fileUrl };
                }
                return file;
              }
            );
          } catch (fileError) {
            console.error("Error fetching file URLs:", fileError);
            // Continue without file URLs if this fails
          }
        }
      }

      // ✅ Set all equipment-related state at once
      setEquipment(normalizedEquipment);
      setEditedEquipment(normalizedEquipment);
      setContacts(normalizedEquipment.contacts || []);
      setOriginalContacts(normalizedEquipment.contacts || []);

      // ✅ Check bookshelf status for carnet equipment
      await checkBookshelfStatus(normalizedEquipment, orgId);

      // ✅ Handle maintenance tasks based on mode
      if (isBookshelfMode) {
        // For bookshelf equipment, maintenance tasks are stored directly in the document
        console.log("Using direct maintenance tasks for bookshelf equipment");
        if (normalizedEquipment.maintenance_tasks) {
          // Add temporary unique IDs to maintenance tasks for UI handling
          const tasksWithIds = normalizedEquipment.maintenance_tasks.map(
            (task, index) => ({
              ...task,
              id: task.id || `bookshelf_task_${Date.now()}_${index}`, // Generate unique ID if not present
            })
          );
          setMaintenanceTasks(tasksWithIds);
          setOriginalMaintenanceTasks(tasksWithIds);
        }
      } else {
        // For carnet equipment, fetch maintenance tasks by IDs
        if (normalizedEquipment.maintenance?.tasks_id) {
          await fetchMaintenanceTasks(normalizedEquipment.maintenance.tasks_id);
        }
      }
    } catch (error) {
      console.error("Error fetching equipment:", error);
      handleClickVariant("Erreur lors du chargement de l'équipement", "error");
    } finally {
      setIsLoading(false);
    }
  }, [equipmentId, open, prefix, isBookshelfMode]);

  const fetchMaintenanceTasks = async (taskIds) => {
    try {
      const tasks = [];
      for (const taskId of taskIds) {
        const taskData = await getDocDataById("maintenance_task", taskId);
        if (taskData) {
          // ✅ Normalize maintenance task dates
          const normalizedTask = normalizeDatesInObject(taskData, [
            "last_performed_date",
            "next_due_date",
            "created_date",
            "updated_date",
          ]);
          tasks.push({ id: taskId, ...normalizedTask });
        }
      }

      // ✅ Additional normalization for all tasks
      const fullyNormalizedTasks = normalizeMaintenanceTasksDates(tasks);
      setMaintenanceTasks(fullyNormalizedTasks);
      setOriginalMaintenanceTasks(fullyNormalizedTasks);
    } catch (error) {
      console.error("Error fetching maintenance tasks:", error);
    }
  };

  const formatDateForInput = (timestamp) => {
    if (!timestamp) return "";

    try {
      // ✅ Since we normalize everything to timestamps, this is simpler
      let date;

      if (typeof timestamp === "number") {
        date = new Date(timestamp);
      } else {
        // Fallback for any non-normalized dates
        date = new Date(normalizeToTimestamp(timestamp));
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "";
      }

      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date for input:", error, timestamp);
      return "";
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Non programmé";

    try {
      // ✅ Since we normalize everything to timestamps, this is simpler
      let date;

      if (typeof timestamp === "number") {
        date = new Date(timestamp);
      } else {
        // Fallback for any non-normalized dates
        date = new Date(normalizeToTimestamp(timestamp));
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Non programmé";
      }

      return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error, timestamp);
      return "Non programmé";
    }
  };

  const handleMaintenanceTaskChange = (taskId, field, value) => {
    setMaintenanceTasks((prev) =>
      prev.map((task) =>
        task.id === taskId
          ? { ...task, [field]: value, hasChanged: true }
          : task
      )
    );
  };

  const handleAddMaintenanceTask = () => {
    const newTask = {
      id: Date.now(), // Temporary ID for new tasks
      name: "Nouvelle tâche de maintenance",
      description: "Description de la tâche",
      frequency_months: 12,
      mandatory: false,
      last_performed_date: null,
      isNew: true,
    };
    setMaintenanceTasks((prev) => [...prev, newTask]);
  };

  const handleRemoveMaintenanceTask = (taskId) => {
    if (maintenanceTasks.find((task) => task.id === taskId)?.isNew) {
      setMaintenanceTasks((prev) => prev.filter((task) => task.id !== taskId));
    } else {
      setDeletedTaskIds((prev) => [...prev, taskId]);
      setMaintenanceTasks((prev) => prev.filter((task) => task.id !== taskId));
    }
  };

  // Effect to fetch equipment when modal opens
  useEffect(() => {
    if (open && equipmentId) {
      fetchEquipment();
    } else if (!open) {
      // Reset state when modal closes
      resetState();
    }
  }, [
    open,
    equipmentId,
    fetchEquipment,
    resetState,
    checkBookshelfStatus,
    orgId,
  ]);

  const handleEdit = () => {
    if (isBookshelfMode) {
      console.log("Editing bookshelf equipment:", equipmentId);
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);

      if (isBookshelfMode) {
        // Handle file operations for bookshelf equipment
        const newFiles =
          editedEquipment.files?.filter((file) => file.isNew && file.file) ||
          [];
        const finalFilesIds =
          editedEquipment.files?.map((fileData) => fileData.id) || [];
        const filesToUploadBase64 =
          newFiles.map((fileData) => fileData.file) || [];

        // Update files if there are changes (new files or file removals)
        if (
          newFiles.length > 0 ||
          finalFilesIds.length !== (equipment.files?.length || 0)
        ) {
          await updateBookshelfEquipmentFiles(
            equipmentId,
            filesToUploadBase64,
            finalFilesIds,
            prefix
          );
        }

        // Prepare bookshelf update payload - this will overwrite the document
        const bookshelfUpdatePayload = {
          name: editedEquipment.name,
          notes: editedEquipment.notes,
          brand: editedEquipment.brand,
          model: editedEquipment.model,
          equipment_type: editedEquipment.equipment_type,
          num_serie: editedEquipment.num_serie,
          lifespan: editedEquipment.lifespan,
          // For bookshelf, maintenance tasks are stored directly as objects
          maintenanceTasks: maintenanceTasks.map((task) => {
            const { id, isNew, hasChanged, ...taskData } = task; // Remove UI-specific fields
            return {
              ...taskData,
              last_performed_date: normalizeToTimestamp(
                task.last_performed_date
              ),
              next_due_date: normalizeToTimestamp(task.next_due_date),
            };
          }),
          customFields: editedEquipment.customFields || {},
          contacts: contacts.map(({ id, ...rest }) => rest), // Remove UI-generated IDs
        };

        console.log(
          "Updating bookshelf equipment:",
          equipmentId,
          bookshelfUpdatePayload
        );
        console.log("Files to upload:", filesToUploadBase64);

        await updateBookshelfEquipment(
          equipmentId,
          bookshelfUpdatePayload,
          prefix
        );
        onRefresh();
        await fetchEquipment();
        setIsEditing(false);
        handleClickVariant(
          "Équipement de bibliothèque mis à jour avec succès",
          "success"
        );
        return;
      }

      // ✅ Separate new files (with File objects) from existing files
      const newFiles =
        editedEquipment.files?.filter((file) => file.isNew && file.file) || [];
      const finalFilesIds =
        editedEquipment.files?.map((fileData) => fileData.id) || [];
      const filesToUploadBase64 =
        newFiles.map((fileData) => fileData.file) || [];

      await updateEquipmentFiles(
        equipmentId,
        filesToUploadBase64,
        finalFilesIds,
        prefix
      );

      // ✅ Prepare the complete update payload
      const updatePayload = {
        name: editedEquipment.name,
        model: editedEquipment.model,
        brand: editedEquipment.brand,
        equipment_type: editedEquipment.equipment_type,
        notes: editedEquipment.notes,
        quantity: editedEquipment.quantity,
        date_livraison: normalizeToTimestamp(editedEquipment.date_livraison),
        lifespan: editedEquipment.lifespan,
        num_serie: editedEquipment.num_serie,
        added_by: editedEquipment.added_by,
        contacts: contacts.map(({ id, ...rest }) => rest),
        customFields: editedEquipment.customFields,
        maintenanceTasks: maintenanceTasks.map((task) => ({
          ...task,
          last_performed_date: normalizeToTimestamp(task.last_performed_date),
          next_due_date: normalizeToTimestamp(task.next_due_date),
          isNew: task.isNew || false,
          isDeleted: deletedTaskIds.includes(task.id) || false,
        })),
      };

      console.log("Updating equipment:", updatePayload);

      await updateEquipment(equipmentId, updatePayload, prefix);
      onRefresh();
      await fetchEquipment();
      setIsEditing(false);
      handleClickVariant("Équipement mis à jour avec succès", "success");
    } catch (error) {
      console.error("Error saving equipment:", error);
      handleClickVariant(
        "Erreur lors de la sauvegarde de l'équipement",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedEquipment(equipment);
    setShowAddContact(false);
    setShowAddField(false);
    setMaintenanceTasks(originalMaintenanceTasks);
    setContacts(originalContacts); // ✅ Reset contacts
    setDeletedTaskIds([]);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (isBookshelfMode) {
      console.log(
        "Opening delete confirmation for bookshelf equipment:",
        equipmentId
      );
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (isBookshelfMode) {
      console.log("Deleting bookshelf equipment:", equipmentId);
      // TODO: Implement bookshelf equipment deletion logic
      setShowDeleteConfirm(false);
      handleClose();
      if (onRefresh) onRefresh();
      return;
    }

    // TODO: Implement carnet equipment delete logic
    console.log("Deleting carnet equipment:", equipmentId);
    setShowDeleteConfirm(false);
    handleClose(); // Close modal after delete
    if (onRefresh) onRefresh();
  };

  const handleFileClick = async (file) => {
    try {
      if (file && file.url) {
        // Open file in a new window/tab
        window.open(file.url, "_blank", "noopener,noreferrer");
        console.log("Opening file:", file.name);
      } else {
        console.error("File URL not found for:", file.name);
      }
    } catch (error) {
      console.error("Error opening file:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    console.log("Files to upload:", files);
    console.log("current files", equipment.files);
    try {
      const uploadedFiles = [];

      for (const file of files) {
        const fileData = {
          id: `${file.name}-${Date.now()}`, // Make ID unique with timestamp
          name: file.name,
          url: URL.createObjectURL(file),
          file: file, // Store the actual File object for uploading
          isNew: true, // Flag to identify new files that need uploading
        };
        uploadedFiles.push(fileData);
      }

      // Update the equipment with new files
      setEditedEquipment((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...uploadedFiles],
      }));

      console.log("Files uploaded successfully:", uploadedFiles);
    } catch (error) {
      console.error("Error uploading files:", error);
      handleClickVariant("Erreur lors du téléchargement des fichiers", "error");
    }
  };

  const handleRemoveFile = (fileId) => {
    const fileToRemove = editedEquipment.files.find(
      (file) => file.id === fileId
    );
    if (
      fileToRemove &&
      fileToRemove.url &&
      fileToRemove.url.startsWith("blob:")
    ) {
      URL.revokeObjectURL(fileToRemove.url);
    }

    let filesUpdated = editedEquipment.files.filter(
      (file) => file.id !== fileId
    );
    setEditedEquipment((prev) => {
      const newState = {
        ...prev,
        files: filesUpdated,
      };
      return newState;
    });
  };

  const handleInputChange = (field, value) => {
    console.log("handleInputChange called:", field, value);
    setEditedEquipment((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      console.log("editedEquipment updated:", updated);
      return updated;
    });
  };

  // Updated handleClose function that resets state
  const handleClose = () => {
    resetState();
    onClose();
  };

  // ✅ Contact management functions - Fixed
  const handleAddContact = () => {
    if (newContact.name && newContact.type) {
      setContacts((prev) => [...prev, { ...newContact, id: Date.now() }]);
      setNewContact({ name: "", type: "", tel: "", email: "" });
      setShowAddContact(false);
    }
  };

  const handleRemoveContact = (contactId) => {
    setContacts((prev) => prev.filter((contact) => contact.id !== contactId));
  };

  const handleAddField = () => {
    if (newField.label && newField.value) {
      setEditedEquipment((prev) => ({
        ...prev,
        customFields: {
          ...(prev.customFields || {}),
          [newField.label]: newField.value,
        },
      }));
      setNewField({ label: "", value: "" });
      setShowAddField(false);
    }
  };

  const handleRemoveField = (fieldId) => {
    setEditedEquipment((prev) => {
      const { [fieldId]: _, ...remainingFields } = prev.customFields || {};
      return {
        ...prev,
        customFields: remainingFields,
      };
    });
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setEditedEquipment((prev) => ({
      ...prev,
      customFields: {
        ...(prev.customFields || {}),
        [fieldId]: value,
      },
    }));
  };

  // Don't render anything if modal is not open
  if (!open) return null;

  // Show loading spinner while fetching data
  if (isLoading && !equipment) {
    return (
      <Modal1 isOpen={open} onClose={handleClose}>
        <div className="w-[1200px] max-w-[90vw] h-[80vh] bg-white rounded-xl overflow-hidden flex items-center justify-center">
          <SmallSpinner />
        </div>
      </Modal1>
    );
  }

  // Don't render content if no equipment data
  if (!equipment) return null;

  return (
    <Modal1 isOpen={open} onClose={handleClose}>
      <div className="w-[1200px] max-w-[90vw] h-[80vh] bg-white rounded-xl overflow-hidden flex flex-col">
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-8 py-6 text-white relative flex-shrink-0">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <Icon1 icon_name="close" size={20} fill="white" />
          </button>

          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Icon1 icon_name="settings" size={24} fill="white" />
            </div>
            <div>
              <h1 className="text-h1 font-bold">
                Fiche descriptive d'équipement
              </h1>
              <p className="text-gray-200  text-h2 mt-1">
                {isEditing ? "Mode édition" : "Informations détaillées"}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="absolute top-4 right-16 flex gap-2">
            {/* Bookshelf Toggle Button - Only show for carnet equipment */}
            {!isBookshelfMode && (
              <button
                onClick={handleBookshelfToggle}
                disabled={bookshelfLoading}
                className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-50"
                title={
                  isInBookshelf
                    ? "Retirer de la bibliothèque"
                    : "Ajouter à la bibliothèque"
                }
              >
                {bookshelfLoading ? (
                  <SmallSpinner />
                ) : (
                  <Icon1
                    icon_name={isInBookshelf ? "bookshelf" : "plus"}
                    size={20}
                    fill="white"
                  />
                )}
              </button>
            )}
            {!isEditing ? (
              <>
                <button
                  onClick={handleEdit}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  title="Modifier"
                >
                  <Icon1 icon_name="modify" size={18} fill="white" />
                </button>
                <button
                  onClick={handleDelete}
                  className="p-2 hover:bg-red-500/30 rounded-full transition-colors"
                  title="Supprimer"
                >
                  <Icon1 icon_name="delete" size={18} fill="white" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="p-2 hover:bg-gray-600 rounded-full transition-colors"
                  title="Sauvegarder"
                  disabled={isLoading}
                >
                  <Icon1 icon_name="done" size={18} fill="white" />
                </button>
                <button
                  onClick={handleCancel}
                  className="p-2 hover:bg-red-500/30 rounded-full transition-colors"
                  title="Annuler"
                  disabled={isLoading}
                >
                  <Icon1 icon_name="close" size={18} fill="white" />
                </button>
              </>
            )}
          </div>
        </div>
        {/* Show loading overlay when saving */}
        {isLoading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
            <SmallSpinner />
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            {/* General Section */}

            <div className="mb-8">
              <h2 className="text-h2 font-semibold text-gray-900 mb-0 border-b border-gray-200 pb-2">
                Général
              </h2>{" "}
              <div className="p-4 mb-4">
                <EquipementImage
                  large={true}
                  equipment={equipment}
                ></EquipementImage>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Equipment Name */}
                <div className="space-y-2">
                  <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                    Nom de l'équipement
                  </label>
                  {isEditing ? (
                    <TextField2T
                      value={editedEquipment?.name || ""}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      className="w-full"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                      <p className="text-h3 font-medium text-gray-900">
                        {equipment.name || "Non spécifié"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Model */}
                <div className="space-y-2">
                  <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                    Modèle
                  </label>
                  {isEditing ? (
                    <TextField2T
                      value={editedEquipment?.model || ""}
                      onChange={(e) =>
                        handleInputChange("model", e.target.value)
                      }
                      className="w-full"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                      <p className="text-h3 font-medium text-gray-900">
                        {equipment.model || "Non spécifié"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Brand */}
                <div className="space-y-2">
                  <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                    Fabricant
                  </label>
                  {isEditing ? (
                    <TextField2T
                      value={editedEquipment?.brand || ""}
                      onChange={(e) =>
                        handleInputChange("brand", e.target.value)
                      }
                      className="w-full"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                      <p className="text-h3 font-medium text-gray-900">
                        {equipment.brand || "Non spécifié"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Equipment Type */}
                <div className="space-y-2">
                  <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                    Type d'équipement
                  </label>
                  {isEditing ? (
                    <EquipmentTypeSelect
                      value={editedEquipment?.equipment_type || ""}
                      onChange={(value) =>
                        handleInputChange("equipment_type", value)
                      }
                      disabled={false}
                      className="w-full"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                      <p className="text-h3 font-medium text-gray-900">
                        {equipment.equipment_type || "Non spécifié"}
                      </p>
                    </div>
                  )}
                </div>

                {/* ✅ Missing Fields Added */}
                {/* Quantity - Hidden in bookshelf mode */}
                {!isBookshelfMode && (
                  <div className="space-y-2">
                    <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                      Quantité
                    </label>
                    {isEditing ? (
                      <TextField2T
                        type="number"
                        value={editedEquipment?.quantity || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "quantity",
                            parseInt(e.target.value) || 0
                          )
                        }
                        className="w-full"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                        <p className="text-h3 font-medium text-gray-900">
                          {equipment.quantity || "Non spécifié"}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Date de livraison - Hidden in bookshelf mode */}
                {!isBookshelfMode && (
                  <div className="space-y-2">
                    <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                      Date de livraison
                    </label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={formatDateForInput(
                          editedEquipment?.date_livraison
                        )}
                        onChange={(e) => {
                          const dateValue = e.target.value;
                          const timestamp = dateValue
                            ? new Date(dateValue).getTime()
                            : null;
                          handleInputChange("date_livraison", timestamp);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                        <p className="text-h3 font-medium text-gray-900">
                          {formatDate(equipment.date_livraison)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Lifespan */}
                <div className="space-y-2">
                  <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                    Durée de vie (années)
                  </label>
                  {isEditing ? (
                    <TextField2T
                      type="number"
                      value={editedEquipment?.lifespan || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "lifespan",
                          parseInt(e.target.value) || 0
                        )
                      }
                      className="w-full"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                      <p className="text-h3 font-medium text-gray-900">
                        {equipment.lifespan
                          ? `${equipment.lifespan} ans`
                          : "Non spécifié"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Numéro de série */}
                <div className="space-y-2">
                  <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                    Numéro de série
                  </label>
                  {isEditing ? (
                    <TextField2T
                      value={editedEquipment?.num_serie || ""}
                      onChange={(e) =>
                        handleInputChange("num_serie", e.target.value)
                      }
                      className="w-full"
                    />
                  ) : (
                    <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                      <p className="text-h3 font-medium text-gray-900">
                        {equipment.num_serie || "Non spécifié"}
                      </p>
                    </div>
                  )}
                </div>

                {/* Added by - Hidden in bookshelf mode */}
                {!isBookshelfMode && (
                  <div className="space-y-2">
                    <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                      Ajouté par
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                      <p className="text-h3 font-medium text-gray-900">
                        {equipment.added_by || "Non spécifié"}
                      </p>
                    </div>
                  </div>
                )}

                {/* ✅ Custom Fields - Fixed to work with list format */}
                {Object.entries(editedEquipment.customFields || {}).map(
                  ([key, value]) => (
                    <div key={key} className="space-y-2 relative">
                      <label className="text-h3 font-semibold text-gray-600 uppercase tracking-wide">
                        {key}
                      </label>
                      {isEditing ? (
                        <div className="relative">
                          <TextField2T
                            value={value}
                            onChange={(e) =>
                              handleCustomFieldChange(key, e.target.value)
                            }
                            className="w-full"
                          />
                          <button
                            onClick={() => handleRemoveField(key)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            title="Supprimer ce champ"
                          >
                            <Icon1 icon_name="close" size={12} fill="white" />
                          </button>
                        </div>
                      ) : (
                        <div className="p-3 bg-gray-50 rounded-lg border h-11 flex items-center">
                          <p className="text-h3 font-medium text-gray-900">
                            {value || "Non spécifié"}
                          </p>
                        </div>
                      )}
                    </div>
                  )
                )}

                {/* Add Field Button */}
                {isEditing && (
                  <div className="space-y-2 flex items-end">
                    <SecondaryButton
                      onClick={() => setShowAddField(true)}
                      startIcon={
                        <Icon1 icon_name="plus" size={16} fill="#6b7280" />
                      }
                      className="w-full"
                    >
                      Ajouter un champ
                    </SecondaryButton>
                  </div>
                )}
              </div>
            </div>
            {/* Information Cards */}
            <div className="grid grid-cols-1 gap-6 mb-8">
              {/* Description */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gray-200 rounded-lg">
                    <Icon1 icon_name="education" size={20} fill="#4b5563" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-h3">
                    Description
                  </h3>
                </div>
                {isEditing ? (
                  <TextField2T
                    value={
                      editedEquipment?.notes ||
                      "Utilisez l'équipement conformément au manuel d'utilisation fourni par le fabricant."
                    }
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    className="w-full"
                  />
                ) : (
                  <p className="text-gray-700  text-h3 leading-relaxed min-h-[44px] flex items-center">
                    {equipment.notes ||
                      "Utilisez l'équipement conformément au manuel d'utilisation fourni par le fabricant."}
                  </p>
                )}
              </div>
            </div>
            {/* Maintenance Section */}
            <div className="border-t pt-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Icon1 icon_name="settings" size={20} fill="#6b7280" />
                  <h3 className="text-h3 font-semibold text-gray-900">
                    Tâches de maintenance
                  </h3>
                </div>
                {isEditing && (
                  <SecondaryButton
                    onClick={handleAddMaintenanceTask}
                    startIcon={
                      <Icon1 icon_name="plus" size={16} fill="#6b7280" />
                    }
                  >
                    Ajouter une tâche
                  </SecondaryButton>
                )}
              </div>

              {maintenanceTasks && maintenanceTasks.length > 0 ? (
                <div className="space-y-4">
                  {maintenanceTasks.map((task) => (
                    <div
                      key={task.id}
                      className="border border-gray-200 rounded-xl p-6 bg-white"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-4">
                            {isEditing ? (
                              <TextField2T
                                value={task.name}
                                onChange={(e) =>
                                  handleMaintenanceTaskChange(
                                    task.id,
                                    "name",
                                    e.target.value
                                  )
                                }
                                className="flex-1"
                              />
                            ) : (
                              <h4 className="font-semibold text-gray-900 flex-1">
                                {task.name}
                              </h4>
                            )}

                            {task.mandatory && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                                Obligatoire
                              </span>
                            )}

                            {isEditing && (
                              <button
                                onClick={() =>
                                  handleRemoveMaintenanceTask(task.id)
                                }
                                className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                title="Supprimer cette tâche"
                              >
                                <Icon1
                                  icon_name="close"
                                  size={12}
                                  fill="white"
                                />
                              </button>
                            )}
                          </div>

                          {isEditing ? (
                            <TextField2T
                              value={task.description}
                              onChange={(e) =>
                                handleMaintenanceTaskChange(
                                  task.id,
                                  "description",
                                  e.target.value
                                )
                              }
                              className="w-full mb-4"
                            />
                          ) : (
                            <p className="text-gray-700 text-h3 mb-4">
                              {task.description}
                            </p>
                          )}

                          {/* ✅ Removed "Dernier entretien" field */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-h3 font-medium text-gray-600 block mb-1">
                                Fréquence (mois)
                              </label>
                              {isEditing ? (
                                <TextField2T
                                  type="number"
                                  value={task.frequency_months}
                                  onChange={(e) =>
                                    handleMaintenanceTaskChange(
                                      task.id,
                                      "frequency_months",
                                      parseInt(e.target.value)
                                    )
                                  }
                                  className="w-full"
                                />
                              ) : (
                                <p className="text-gray-900">
                                  Tous les {task.frequency_months} mois
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="text-h3 font-medium text-gray-600 block mb-1">
                                Obligatoire
                              </label>
                              {isEditing ? (
                                <div className="flex items-center mt-2">
                                  <input
                                    type="checkbox"
                                    checked={task.mandatory}
                                    onChange={(e) =>
                                      handleMaintenanceTaskChange(
                                        task.id,
                                        "mandatory",
                                        e.target.checked
                                      )
                                    }
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <span className="ml-2 text-h3 text-gray-700">
                                    Tâche obligatoire
                                  </span>
                                </div>
                              ) : (
                                <p className="text-gray-900">
                                  {task.mandatory ? "Oui" : "Non"}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 text-h3 rounded-xl p-6 text-center">
                  <Icon1 icon_name="settings" size={24} fill="#9ca3af" />
                  <p className="text-gray-500 mt-2">
                    Aucune tâche de maintenance définie
                  </p>
                  {isEditing && (
                    <p className="text-gray-400 text-h3 mt-1">
                      Cliquez sur "Ajouter une tâche" pour créer les tâches de
                      maintenance
                    </p>
                  )}
                </div>
              )}
            </div>
            {/* Files Section */}
            <div className="border-t pt-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Icon1 icon_name="file" size={20} fill="#6b7280" />
                  <h3 className="text-h3 font-semibold text-gray-900">
                    Documents associés
                  </h3>
                </div>
                {isEditing && (
                  <div>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    <label htmlFor="file-upload">
                      <SecondaryButton
                        startIcon={
                          <Icon1 icon_name="upload" size={16} fill="#6b7280" />
                        }
                        onClick={() =>
                          document.getElementById("file-upload").click()
                        }
                      >
                        Ajouter des fichiers
                      </SecondaryButton>
                    </label>
                  </div>
                )}
              </div>
              {editedEquipment.files && editedEquipment.files.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {editedEquipment.files.map((file) => (
                    <div
                      key={file.id}
                      className="p-4 bg-gray-50 border border-gray-200 rounded-lg group relative"
                    >
                      {isEditing && (
                        <button
                          onClick={() => handleRemoveFile(file.id)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          title="Supprimer ce fichier"
                        >
                          <Icon1 icon_name="close" size={12} fill="white" />
                        </button>
                      )}
                      <button
                        onClick={() => handleFileClick(file)}
                        className="flex items-center gap-3 w-full text-left hover:bg-gray-100 rounded p-2 transition-colors"
                      >
                        <div className="p-2 bg-gray-200 rounded-lg group-hover:bg-gray-300 transition-colors">
                          <Icon1
                            icon_name="document"
                            size={16}
                            fill="#4b5563"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-h3 font-medium text-gray-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Cliquer pour télécharger
                          </p>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 text-h3 rounded-xl p-6 text-center">
                  <Icon1 icon_name="file" size={24} fill="#9ca3af" />
                  <p className="text-gray-500 mt-2">Aucun document associé</p>
                </div>
              )}
            </div>
            {/* Contacts Section */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Icon1 icon_name="phone" size={20} fill="#6b7280" />
                  <h3 className="text-h3 font-semibold text-gray-900">
                    Contacts
                  </h3>
                </div>
                {isEditing && (
                  <SecondaryButton
                    onClick={() => setShowAddContact(true)}
                    startIcon={
                      <Icon1 icon_name="plus" size={16} fill="#6b7280" />
                    }
                  >
                    Ajouter un contact
                  </SecondaryButton>
                )}
              </div>

              {/* ✅ Fixed to use contacts state */}
              {contacts.length > 0 ? (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="bg-gray-50 border border-gray-200 rounded-xl p-6 relative"
                    >
                      {isEditing && (
                        <button
                          onClick={() => handleRemoveContact(contact.id)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          title="Supprimer ce contact"
                        >
                          <Icon1 icon_name="close" size={12} fill="white" />
                        </button>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <h4 className="text-h3 font-semibold text-gray-600 uppercase tracking-wide mb-1">
                            Nom
                          </h4>
                          <p className="text-gray-900 font-medium">
                            {contact.name}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-h3 font-semibold text-gray-600 uppercase tracking-wide mb-1">
                            Type
                          </h4>
                          <p className="text-gray-900">{contact.type}</p>
                        </div>
                        <div>
                          <h4 className="text-h3 font-semibold text-gray-600 uppercase tracking-wide mb-1">
                            Téléphone
                          </h4>
                          <p className="text-gray-900">
                            {contact.tel || "Non renseigné"}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-h3 font-semibold text-gray-600 uppercase tracking-wide mb-1">
                            Email
                          </h4>
                          <p className="text-gray-900">
                            {contact.email || "Non renseigné"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl text-h3 p-6 text-center">
                  <Icon1 icon_name="phone" size={24} fill="#9ca3af" />
                  <p className="text-gray-500 mt-2">Aucun contact enregistré</p>
                </div>
              )}
            </div>
            {/* Action Buttons */}
            {isEditing && (
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
                <SecondaryButton onClick={handleCancel} disabled={isLoading}>
                  Annuler
                </SecondaryButton>
                <PrimaryButton onClick={handleSave} disabled={isLoading}>
                  {isLoading
                    ? "Sauvegarde..."
                    : "Sauvegarder les modifications"}
                </PrimaryButton>
              </div>
            )}
          </div>
        </div>
        {/* Maintenance History Modal */}
        {showMaintenanceHistory && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Icon1 icon_name="rapport" size={24} fill="#6b7280" />
                  <h3 className="text-h3 font-semibold text-gray-900">
                    Historique de maintenance
                  </h3>
                </div>
                <button
                  onClick={() => setShowMaintenanceHistory(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <Icon1 icon_name="close" size={20} fill="#6b7280" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-center py-8">
                  <Icon1 icon_name="rapport" size={48} fill="#9ca3af" />
                  <p className="text-gray-500 mt-4">
                    Historique de maintenance
                  </p>
                  <p className="text-gray-400 text-h3">
                    L'historique des maintenances effectuées apparaîtra ici
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Add Contact Modal */}
        {showAddContact && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <Icon1 icon_name="plus" size={24} fill="#6b7280" />
                <h3 className="text-h3 font-semibold text-gray-900">
                  Ajouter un contact
                </h3>
              </div>
              <div className="space-y-4">
                <TextField2T
                  label="Nom du contact"
                  value={newContact.name}
                  onChange={(e) =>
                    setNewContact((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
                <TextField2T
                  label="Type (ex: SAV, Support, Commercial)"
                  value={newContact.type}
                  onChange={(e) =>
                    setNewContact((prev) => ({ ...prev, type: e.target.value }))
                  }
                />
                <TextField2T
                  label="Téléphone"
                  value={newContact.tel}
                  onChange={(e) =>
                    setNewContact((prev) => ({ ...prev, tel: e.target.value }))
                  }
                />
                <TextField2T
                  label="Email"
                  value={newContact.email}
                  onChange={(e) =>
                    setNewContact((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <SecondaryButton onClick={() => setShowAddContact(false)}>
                  Annuler
                </SecondaryButton>
                <PrimaryButton onClick={handleAddContact}>
                  Ajouter
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
        {/* Add Field Modal */}
        {showAddField && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <Icon1 icon_name="plus" size={24} fill="#6b7280" />
                <h3 className="text-h3 font-semibold text-gray-900">
                  Ajouter un champ personnalisé
                </h3>
              </div>
              <div className="space-y-4">
                <TextField2T
                  label="Nom du champ"
                  value={newField.label}
                  onChange={(e) =>
                    setNewField((prev) => ({ ...prev, label: e.target.value }))
                  }
                />
                <TextField2T
                  label="Valeur"
                  value={newField.value}
                  onChange={(e) =>
                    setNewField((prev) => ({ ...prev, value: e.target.value }))
                  }
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <SecondaryButton onClick={() => setShowAddField(false)}>
                  Annuler
                </SecondaryButton>
                <PrimaryButton onClick={handleAddField}>Ajouter</PrimaryButton>
              </div>
            </div>
          </div>
        )}
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Icon1 icon_name="warning" size={24} fill="#ef4444" />
                </div>
                <h3 className="text-h3 font-semibold text-gray-900">
                  Confirmer la suppression
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer cet équipement ? Cette action
                est irréversible.
              </p>
              <div className="flex justify-end gap-3">
                <SecondaryButton onClick={() => setShowDeleteConfirm(false)}>
                  Annuler
                </SecondaryButton>
                <PrimaryButton
                  onClick={confirmDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Supprimer définitivement
                </PrimaryButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal1>
  );
};
